import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Label } from "../../components/ui/label";
import { ToggleSwitch as Switch } from "../../components/ui/toggle-switch";
import {
  Save, Loader2, Bot, MapPin, Package, Percent, Plus, Trash2, Tag,
  Clock, Pencil, Check, X
} from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrezziPanel = () => {
  const confirm = useConfirm();
  const [packages, setPackages] = useState([]);
  const [comboRules, setComboRules] = useState(null);
  const [addonConfig, setAddonConfig] = useState(null);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editCode, setEditCode] = useState(null);
  const [codeForm, setCodeForm] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem("sa_token")}` };

  // Sessione SuperAdmin scaduta / token non valido → re-login pulito
  const handleSaError = (err, fallback) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem("sa_token");
      toast.error("Sessione scaduta. Effettua di nuovo l'accesso.");
      setTimeout(() => { window.location.href = "/superadmin"; }, 1200);
      return;
    }
    toast.error(err?.response?.data?.detail || fallback);
  };

  const fetchData = useCallback(async () => {
    try {
      const [pkgRes, comboRes, addonRes, codesRes] = await Promise.all([
        axios.get(`${API}/superadmin/packages`, { headers }),
        axios.get(`${API}/superadmin/combo-rules`, { headers }),
        axios.get(`${API}/superadmin/addon-config`, { headers }),
        axios.get(`${API}/superadmin/discount-codes`, { headers }),
      ]);
      setPackages(pkgRes.data);
      setComboRules(comboRes.data);
      setAddonConfig(addonRes.data);
      setDiscountCodes(codesRes.data);
    } catch (err) { handleSaError(err, "Errore caricamento prezzi"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveAll = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/superadmin/combo-rules`, comboRules, { headers }),
        axios.put(`${API}/superadmin/addon-config`, addonConfig, { headers }),
      ]);
      toast.success("Prezzi aggiornati!");
    } catch (err) { handleSaError(err, "Errore salvataggio"); }
    setSaving(false);
  };

  const updateRule = (idx, field, value) => {
    setComboRules(prev => {
      const regole = [...prev.regole];
      regole[idx] = { ...regole[idx], [field]: value };
      return { ...prev, regole };
    });
  };

  const addRule = () => {
    setComboRules(prev => ({
      ...prev,
      regole: [...prev.regole, { num_pacchetti: 2, include_acquisti: false, prezzo_mensile: 0, label: "" }]
    }));
  };

  const removeRule = (idx) => {
    setComboRules(prev => ({ ...prev, regole: prev.regole.filter((_, i) => i !== idx) }));
  };

  const emptyCode = { codice: "", tipo_sconto: "percentuale", valore: 0, applica_a: "tutti", durata_sconto: "primo_mese", max_utilizzi: 0, scadenza: "", note: "", attivo: true };

  const openCodeForm = (code = null) => {
    setEditCode(code);
    setCodeForm(code ? { ...emptyCode, ...code } : { ...emptyCode });
  };

  const saveCode = async () => {
    if (!codeForm.codice.trim()) { toast.error("Inserisci il codice"); return; }
    try {
      if (editCode) {
        await axios.put(`${API}/superadmin/discount-codes/${editCode.id}`, codeForm, { headers });
      } else {
        await axios.post(`${API}/superadmin/discount-codes`, codeForm, { headers });
      }
      toast.success(editCode ? "Codice aggiornato!" : "Codice creato!");
      setCodeForm(null);
      fetchData();
    } catch (err) { handleSaError(err, "Errore"); }
  };

  const deleteCode = async (id) => {
    if (!await confirm("Eliminare questo codice?")) return;
    try {
      await axios.delete(`${API}/superadmin/discount-codes/${id}`, { headers });
      toast.success("Eliminato");
      fetchData();
    } catch (err) { handleSaError(err, "Errore"); }
  };

  const toggleCodeActive = async (code) => {
    try {
      await axios.put(`${API}/superadmin/discount-codes/${code.id}`, { attivo: !code.attivo }, { headers });
      fetchData();
    } catch (err) { handleSaError(err, "Errore"); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>;

  const tg = addonConfig?.telegram_bot || {};
  const loc = addonConfig?.locale_aggiuntivo || {};

  return (
    <div className="space-y-8" data-testid="prezzi-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black tracking-tight text-slate-900">Gestione Prezzi</h2>
          <p className="text-sm text-slate-500 mt-0.5">Tutti i prezzi sono netti. L'IVA 22% viene aggiunta al checkout.</p>
        </div>
        <Button onClick={saveAll} disabled={saving}
          className="rounded-xl bg-slate-900 hover:bg-slate-700 text-white hover:text-white font-semibold px-6"
          data-testid="save-all-btn">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Salvando..." : "Salva tutto"}
        </Button>
      </div>

      {/* ═══ SEZIONE 1: PACCHETTI SINGOLI (sola lettura) ═══ */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Package className="h-5 w-5 text-indigo-500" />
          <h3 className="text-lg font-bold text-slate-900">Pacchetti Singoli</h3>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 font-bold">MODIFICA DA "PACCHETTI"</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {packages.filter(p => p.tipo === "base").map(pkg => (
            <div key={pkg.id} className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm" data-testid={`pkg-price-${pkg.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-bold text-slate-800">{pkg.nome}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${pkg.tipo === "base" ? "bg-indigo-50 text-indigo-600" : "bg-amber-50 text-amber-600"}`}>{pkg.tipo?.toUpperCase()}</span>
              </div>
              <p className="text-2xl font-black text-slate-900">{pkg.prezzo_mensile_annuale}€<span className="text-sm font-normal text-slate-400">/mese</span></p>
              {pkg.sconto_percentuale > 0 && <p className="text-xs text-emerald-600 font-semibold mt-1">Sconto registrazione: -{pkg.sconto_percentuale}%</p>}
            </div>
          ))}
        </div>
      </section>

      {/* ═══ SEZIONE 2: REGOLE COMBO ═══ */}
      {comboRules && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Package className="h-5 w-5 text-violet-500" />
            <h3 className="text-lg font-bold text-slate-900">Regole Combo</h3>
          </div>
          <p className="text-sm text-slate-500 mb-3">Se il cliente seleziona più pacchetti, il prezzo combo sostituisce la somma dei singoli.</p>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-slate-50 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
              <span className="col-span-2">N. Pacchetti</span>
              <span className="col-span-3">Etichetta</span>
              <span className="col-span-2">Prezzo/mese</span>
              <span className="col-span-3">Condizione</span>
              <span className="col-span-2 text-right">Azioni</span>
            </div>
            {comboRules.regole?.map((rule, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 px-5 py-3 items-center border-b border-slate-50 last:border-b-0" data-testid={`combo-rule-${idx}`}>
                <div className="col-span-2">
                  <Input type="number" min={2} value={rule.num_pacchetti}
                    onChange={e => updateRule(idx, "num_pacchetti", parseInt(e.target.value) || 2)}
                    className="h-9 rounded-lg text-center" />
                </div>
                <div className="col-span-3">
                  <Input value={rule.label || ""} onChange={e => updateRule(idx, "label", e.target.value)}
                    className="h-9 rounded-lg" placeholder="Es. Duo Pack" />
                </div>
                <div className="col-span-2">
                  <div className="relative">
                    <Input type="number" step="0.5" min={0} value={rule.prezzo_mensile}
                      onChange={e => updateRule(idx, "prezzo_mensile", parseFloat(e.target.value) || 0)}
                      className="h-9 rounded-lg pr-6" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">€</span>
                  </div>
                </div>
                <div className="col-span-3">
                  {rule.num_pacchetti < 3 && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch checked={rule.include_acquisti || false}
                        onCheckedChange={v => updateRule(idx, "include_acquisti", v)} />
                      <span className="text-xs text-slate-600">Con Acquisti</span>
                    </label>
                  )}
                  {rule.num_pacchetti >= 3 && <span className="text-xs text-slate-400">Tutti i pacchetti</span>}
                </div>
                <div className="col-span-2 text-right">
                  <Button size="icon" variant="ghost" onClick={() => removeRule(idx)} className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            <div className="px-5 py-3 border-t border-slate-100">
              <Button variant="ghost" onClick={addRule} className="text-xs text-slate-500 hover:text-slate-700 gap-1" data-testid="add-combo-rule-btn">
                <Plus className="h-3.5 w-3.5" /> Aggiungi regola combo
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* ═══ SEZIONE 3: BOT TELEGRAM ═══ */}
      {addonConfig && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Bot className="h-5 w-5 text-blue-500" />
            <h3 className="text-lg font-bold text-slate-900">Bot Telegram</h3>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Costo 1° numero (€/mese netto)</Label>
                <Input type="number" step="0.5" min={0} value={tg.prezzo_primo_numero ?? 10}
                  onChange={e => {
                    const v = parseFloat(e.target.value) || 0;
                    // Allinea anche prezzo_mensile (campo legacy usato in alcuni punti del backend)
                    setAddonConfig(c => ({ ...c, telegram_bot: { ...c.telegram_bot, prezzo_primo_numero: v, prezzo_mensile: v } }));
                  }}
                  className="mt-1 rounded-xl" data-testid="tg-prezzo-primo" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Costo numeri aggiuntivi (€/mese cad.)</Label>
                <Input type="number" step="0.5" min={0} value={tg.prezzo_numero_extra ?? 5}
                  onChange={e => setAddonConfig(c => ({ ...c, telegram_bot: { ...c.telegram_bot, prezzo_numero_extra: parseFloat(e.target.value) || 0 } }))}
                  className="mt-1 rounded-xl" data-testid="tg-prezzo-extra" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">Esempio: 1 numero = {(tg.prezzo_primo_numero ?? 10).toFixed(2)}€ · 3 numeri = {((tg.prezzo_primo_numero ?? 10) + 2 * (tg.prezzo_numero_extra ?? 5)).toFixed(2)}€</p>
          </div>
        </section>
      )}

      {/* ═══ SEZIONE 4: LOCALE EXTRA ═══ */}
      {addonConfig && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-5 w-5 text-orange-500" />
            <h3 className="text-lg font-bold text-slate-900">Locale Extra</h3>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Locali inclusi nel piano (gratis)</Label>
                <Input type="number" min={0} value={loc.locali_inclusi ?? 1}
                  onChange={e => setAddonConfig(c => ({ ...c, locale_aggiuntivo: { ...c.locale_aggiuntivo, locali_inclusi: parseInt(e.target.value) || 0 } }))}
                  className="mt-1 rounded-xl" data-testid="loc-inclusi" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Prezzo per ogni locale extra (€/mese netto)</Label>
                <Input type="number" step="0.5" min={0} value={loc.prezzo_extra_annuale ?? 20}
                  onChange={e => setAddonConfig(c => ({ ...c, locale_aggiuntivo: { ...c.locale_aggiuntivo, prezzo_extra_annuale: parseFloat(e.target.value) || 0 } }))}
                  className="mt-1 rounded-xl" data-testid="loc-price" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ═══ SEZIONE 5: CODICI SCONTO ═══ */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-emerald-500" />
            <h3 className="text-lg font-bold text-slate-900">Codici Sconto</h3>
            <span className="text-xs text-slate-400">({discountCodes.length})</span>
          </div>
          <Button onClick={() => openCodeForm()} variant="outline" className="rounded-xl text-xs font-semibold gap-1 border-dashed" data-testid="add-discount-btn">
            <Plus className="h-3.5 w-3.5" /> Nuovo Codice
          </Button>
        </div>

        {/* Code form */}
        {codeForm && (
          <div className="bg-white rounded-2xl p-5 border-2 border-indigo-200 shadow-sm mb-4 space-y-4" data-testid="discount-form">
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-slate-900">{editCode ? "Modifica codice" : "Nuovo codice sconto"}</h4>
              <Button size="icon" variant="ghost" onClick={() => setCodeForm(null)} className="h-7 w-7"><X className="h-4 w-4" /></Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Codice</Label>
                <Input value={codeForm.codice} onChange={e => setCodeForm(p => ({ ...p, codice: e.target.value.toUpperCase() }))}
                  className="mt-1 rounded-lg font-mono" placeholder="ES. BENVENUTO" data-testid="code-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Tipo sconto</Label>
                <select value={codeForm.tipo_sconto} onChange={e => setCodeForm(p => ({ ...p, tipo_sconto: e.target.value }))}
                  className="w-full h-10 mt-1 px-3 rounded-lg bg-white border border-slate-200 text-sm">
                  <option value="percentuale">Percentuale (%)</option>
                  <option value="fisso">Fisso (€)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Valore</Label>
                <Input type="number" step="0.5" value={codeForm.valore} onChange={e => setCodeForm(p => ({ ...p, valore: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 rounded-lg" data-testid="code-value" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Durata sconto</Label>
                <select value={codeForm.durata_sconto} onChange={e => setCodeForm(p => ({ ...p, durata_sconto: e.target.value }))}
                  className="w-full h-10 mt-1 px-3 rounded-lg bg-white border border-slate-200 text-sm">
                  <option value="primo_mese">Solo primo mese</option>
                  <option value="ricorrente">Ricorrente</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Applica a</Label>
                <select value={codeForm.applica_a} onChange={e => setCodeForm(p => ({ ...p, applica_a: e.target.value }))}
                  className="w-full h-10 mt-1 px-3 rounded-lg bg-white border border-slate-200 text-sm">
                  <option key="tutti" value="tutti">Tutti i pacchetti</option>
                  <optgroup key="grp-addon" label="Add-on">
                    <option key="bot_telegram" value="bot_telegram">Bot Telegram (add-on)</option>
                  </optgroup>
                  <optgroup key="grp-pkg" label="Pacchetti">
                    {packages.filter(p => p.tipo === "base").map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                  </optgroup>
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Max utilizzi (0=illim.)</Label>
                <Input type="number" min={0} value={codeForm.max_utilizzi} onChange={e => setCodeForm(p => ({ ...p, max_utilizzi: parseInt(e.target.value) || 0 }))}
                  className="mt-1 rounded-lg" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Scadenza</Label>
                <DateInput value={codeForm.scadenza?.split("T")[0] || ""} onChange={(v) => setCodeForm(p => ({ ...p, scadenza: v }))} />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Note</Label>
                <Input value={codeForm.note || ""} onChange={e => setCodeForm(p => ({ ...p, note: e.target.value }))}
                  className="mt-1 rounded-lg" placeholder="Nota interna" />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={saveCode} className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white hover:text-white font-semibold" data-testid="save-code-btn">
                <Check className="h-4 w-4 mr-1" /> {editCode ? "Aggiorna" : "Crea codice"}
              </Button>
              <Button variant="outline" onClick={() => setCodeForm(null)} className="rounded-xl">Annulla</Button>
            </div>
          </div>
        )}

        {/* Code list */}
        {discountCodes.length === 0 && !codeForm ? (
          <div className="bg-white rounded-2xl p-8 text-center border border-slate-200 shadow-sm">
            <Tag className="h-8 w-8 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-400">Nessun codice sconto. Creane uno per attirare nuovi clienti.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {discountCodes.map(code => (
              <div key={code.id} className={`bg-white rounded-2xl border shadow-sm transition-opacity ${code.attivo ? 'border-slate-200' : 'border-slate-100 opacity-50'}`} data-testid={`discount-${code.id}`}>
                <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Switch checked={code.attivo} onCheckedChange={() => toggleCodeActive(code)} />
                    <span className="font-mono font-bold text-slate-900 text-sm">{code.codice}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Percent className="h-3 w-3" />
                      {code.tipo_sconto === "percentuale" ? `-${code.valore}%` : `-${code.valore}€`}
                    </span>
                    <span className="flex items-center gap-1">
                      <Package className="h-3 w-3" />
                      {code.applica_a === "tutti" ? "Tutti" : code.applica_a === "bot_telegram" ? "Bot Telegram" : packages.find(p => p.id === code.applica_a)?.nome || code.applica_a}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {code.durata_sconto === "primo_mese" ? "1° mese" : "Ricorrente"}
                    </span>
                    {code.max_utilizzi > 0 && <span>{code.utilizzi_reali ?? code.utilizzi ?? 0}/{code.max_utilizzi} usi</span>}
                    {code.scadenza && <span>Scade: {code.scadenza.split("T")[0]}</span>}
                    <span className="flex items-center gap-1 font-semibold text-emerald-600" data-testid={`discount-usage-count-${code.id}`}>
                      <Tag className="h-3 w-3" />
                      {code.utilizzi_reali ?? 0} {(code.utilizzi_reali ?? 0) === 1 ? "utilizzo" : "utilizzi"}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  <Button size="icon" variant="ghost" onClick={() => openCodeForm(code)} className="h-7 w-7 text-slate-400 hover:text-slate-700"><Pencil className="h-3 w-3" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => deleteCode(code.id)} className="h-7 w-7 text-slate-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></Button>
                </div>
                </div>
                {code.usages?.length > 0 && (
                  <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/60 rounded-b-2xl" data-testid={`discount-usages-${code.id}`}>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Utilizzato da</p>
                    <div className="space-y-1.5">
                      {code.usages.map((u, i) => (
                        <div key={u.id || i} className="flex items-center justify-between gap-2 text-xs" data-testid={`usage-row-${code.id}-${i}`}>
                          <span className="font-medium text-slate-700 truncate">{u.denominazione || u.email || "—"}</span>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-emerald-600 font-semibold">
                              {u.tipo_sconto === "percentuale" ? `-${u.valore}%` : `-${u.valore}€`}
                            </span>
                            <span className="text-slate-400 tabular-nums">{u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : ""}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default PrezziPanel;
